import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getApi, downloadApi } from '../../../Services/apiService';
import { getCheckrStatusLabel } from '../../../Utils/checkrStatus';
// import { CHECKR_STATUS, CHECKR_PACKAGE } from '../../../Utils/checkrStatus'; // for the hidden ZIP download

// downloadApi uses responseType blob, so an error body has to be parsed to read the message
export async function getBlobErrorMessage(error, fallback) {
  const errorData = error?.response?.data;
  if (errorData instanceof Blob) {
    try {
      const parsed = JSON.parse(await errorData.text());
      if (parsed?.message) return parsed.message;
    } catch (_) { /* not JSON */ }
  }
  return fallback;
}

function formatCheckrTimestamp(value) {
  if (value == null || value === '') return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Sections of a completed Checkr report (GET /verification/tenant/checkr/verification-result)
const CHECKR_REPORT_SECTIONS = [
  { key: 'criminal_history', label: 'Criminal History' },
  { key: 'credit_report', label: 'Credit Report' },
  { key: 'eviction_history', label: 'Eviction History' },
  { key: 'sex_offender_registry', label: 'Sex Offender Registry' },
  { key: 'global_watchlist', label: 'Global Watchlist' },
  // Only part of some packages: shown only when the report has data for them
  { key: 'identity_verification', label: 'Identity Verification', optional: true },
  { key: 'income_verification', label: 'Income Verification', optional: true },
];
// Report-level metadata, not sections
const CHECKR_REPORT_META_KEYS = new Set(['id', 'object', 'uri', 'live_mode', 'order_id', 'created_at', 'updated_at', 'completed_at', 'status', 'result']);

/** Known sections in order (optional ones only when present), then any other section the report carries. */
function getCheckrReportSections(report) {
  if (!report || typeof report !== 'object') return [];
  const known = CHECKR_REPORT_SECTIONS.filter(({ key, optional }) => !optional || report[key] != null);
  const knownKeys = new Set(CHECKR_REPORT_SECTIONS.map(({ key }) => key));
  const extra = Object.entries(report)
    .filter(([key, value]) => !knownKeys.has(key) && !CHECKR_REPORT_META_KEYS.has(key) && value != null && typeof value === 'object')
    .map(([key]) => ({ key, label: formatCheckrKey(key) }));
  return [...known, ...extra];
}

// `{ records: [...] }` wrappers (e.g. collections, bankruptcies) are shown as the list itself
function unwrapCheckrRecords(value) {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return value;
  const keys = Object.keys(value).filter((key) => !CHECKR_HIDDEN_FIELDS.has(key));
  return keys.length === 1 && keys[0] === 'records' && Array.isArray(value.records) ? value.records : value;
}

// e.g. credit_score_factors: [{ code, description }]
function isCheckrCodeDescription(item) {
  return item != null && typeof item === 'object' && 'description' in item
    && Object.keys(item).every((key) => key === 'code' || key === 'description');
}

// Bookkeeping fields shown in the section header (or not at all) instead of the body
const CHECKR_HIDDEN_FIELDS = new Set(['id', 'object', 'uri', 'status', 'result', 'created_at', 'updated_at', 'completed_at']);
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/;

function formatCheckrKey(key) {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Money fields come as *_cents; the label drops the suffix since the value is shown in dollars
function formatCheckrLabel(key) {
  return formatCheckrKey(String(key).replace(/_cents$/, ''));
}

function formatCheckrFieldValue(key, value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    if (/_cents$/.test(key)) {
      return (value / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
    }
    if (/(^|_)rate$/.test(key)) return `${value}%`;
    return value.toLocaleString('en-US');
  }
  const str = String(value);
  if (ISO_DATE_RE.test(str)) return formatCheckrTimestamp(str);
  // enum-like values (e.g. "available", "not_found") read better capitalised
  if (/^[a-z]+(_[a-z]+)*$/.test(str)) return formatCheckrKey(str);
  return str;
}

function getCreditScoreBand(score) {
  if (score >= 800) return { label: 'Exceptional', variant: 'excellent' };
  if (score >= 740) return { label: 'Very Good', variant: 'excellent' };
  if (score >= 670) return { label: 'Good', variant: 'good' };
  if (score >= 580) return { label: 'Fair', variant: 'fair' };
  return { label: 'Poor', variant: 'poor' };
}

function CheckrStatusPill({ status }) {
  if (!status) return null;
  const key = String(status).toLowerCase();
  const variant = key === 'clear' ? 'clear' : key === 'consider' ? 'consider' : 'neutral';
  return <span className={`checkr-pill checkr-pill-${variant}`}>{formatCheckrKey(key)}</span>;
}

function CheckrUtilizationBar({ rate }) {
  const pct = Math.max(0, Math.min(100, Number(rate) || 0));
  const variant = pct <= 30 ? 'good' : pct <= 50 ? 'fair' : 'poor';
  return (
    <div className="checkr-util-bar" aria-hidden="true">
      <div className={`checkr-util-bar-fill checkr-util-bar-fill-${variant}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Tiles for primitives; nested objects become grouped tiles; arrays become record lists. */
function CheckrFieldGrid({ data }) {
  const entries = Object.entries(data || {})
    .filter(([key]) => !CHECKR_HIDDEN_FIELDS.has(key))
    .map(([key, value]) => [key, unwrapCheckrRecords(value)]);
  const primitives = entries.filter(([, v]) => v == null || typeof v !== 'object');
  const groups = entries.filter(([, v]) => v != null && typeof v === 'object' && !Array.isArray(v));
  const lists = entries.filter(([, v]) => Array.isArray(v));

  return (
    <>
      {primitives.length > 0 && (
        <div className="checkr-stats">
          {primitives.map(([key, value]) => (
            <div key={key} className="checkr-stat">
              <span className="checkr-stat-label">{formatCheckrLabel(key)}</span>
              <span className={`checkr-stat-value${value == null || value === '' ? ' checkr-stat-value-muted' : ''}`}>
                {formatCheckrFieldValue(key, value)}
              </span>
            </div>
          ))}
        </div>
      )}
      {groups.map(([key, value]) => (
        <div key={key} className="checkr-group">
          <div className="checkr-group-header">
            <span className="checkr-group-title">{formatCheckrLabel(key)}</span>
            {typeof value.rate === 'number' && <span className="checkr-group-figure">{value.rate}%</span>}
          </div>
          {typeof value.rate === 'number' && <CheckrUtilizationBar rate={value.rate} />}
          {typeof value.rate === 'number' && typeof value.balance_cents === 'number' && typeof value.limit_cents === 'number' ? (
            <p className="checkr-group-caption">
              {formatCheckrFieldValue('balance_cents', value.balance_cents)} used of{' '}
              {formatCheckrFieldValue('limit_cents', value.limit_cents)} limit
            </p>
          ) : (
            <CheckrFieldGrid data={value} />
          )}
        </div>
      ))}
      {lists.map(([key, value]) => (
        <div key={key} className="checkr-group">
          <div className="checkr-group-header">
            <span className="checkr-group-title">{formatCheckrLabel(key)}</span>
            <span className="checkr-group-count">{value.length}</span>
          </div>
          {value.length === 0 ? (
            <p className="checkr-no-records">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              No {formatCheckrLabel(key).toLowerCase()} found
            </p>
          ) : value.every(isCheckrCodeDescription) ? (
            <ul className="checkr-factors">
              {value.map((item, index) => (
                <li key={index} className="checkr-factor">
                  {item.code != null && item.code !== '' && <span className="checkr-factor-code">{item.code}</span>}
                  <span>{item.description || '—'}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="checkr-records">
              {value.map((item, index) => (
                <div key={index} className="checkr-record">
                  {item != null && typeof item === 'object' ? (
                    <>
                      {(item.result || item.status) && (
                        <div className="checkr-record-header">
                          <span>#{index + 1}</span>
                          <CheckrStatusPill status={item.result || item.status} />
                        </div>
                      )}
                      <CheckrFieldGrid data={item} />
                    </>
                  ) : (
                    <span className="checkr-stat-value">{formatCheckrFieldValue(key, item)}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

function getCheckrSectionStatus(data) {
  return data && typeof data === 'object' ? data.result ?? data.status ?? null : null;
}

/** One collapsible row per report section; the body stays mounted so printing shows every section. */
function CheckrReportSection({ label, data, isOpen, onToggle }) {
  if (!data || typeof data !== 'object') {
    return (
      <section className="checkr-section checkr-section-empty">
        <div className="checkr-section-toggle">
          <span className="checkr-section-heading">
            <h3 className="checkr-section-title">{label}</h3>
            <span className="checkr-section-meta">Not included in this screening</span>
          </span>
          <span className="checkr-overview-na">N/A</span>
        </div>
      </section>
    );
  }
  const status = getCheckrSectionStatus(data);
  const checkedAt = data.completed_at ?? data.updated_at ?? data.created_at;
  const { credit_score: creditScore, credit_file_status: creditFileStatus, ...rest } = data;
  const hasScore = typeof creditScore === 'number';
  const band = hasScore ? getCreditScoreBand(creditScore) : null;

  return (
    <section className={`checkr-section${isOpen ? ' checkr-section-open' : ''}`}>
      <button type="button" className="checkr-section-toggle" onClick={onToggle} aria-expanded={isOpen}>
        <span className="checkr-section-heading">
          <h3 className="checkr-section-title">{label}</h3>
          {checkedAt && <span className="checkr-section-meta">Checked {formatCheckrTimestamp(checkedAt)}</span>}
        </span>
        {hasScore && <span className={`checkr-section-score checkr-section-score-${band.variant}`}>{creditScore}</span>}
        <CheckrStatusPill status={status} />
        <svg className="checkr-section-chevron checkr-no-print" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <div className="checkr-section-body">
        {hasScore && (
          <div className={`checkr-score checkr-score-${band.variant}`}>
            <div className="checkr-score-number">{creditScore}</div>
            <div className="checkr-score-info">
              <span className="checkr-score-band">{band.label}</span>
              <span className="checkr-score-caption">
                Credit score{creditFileStatus ? ` · File ${formatCheckrFieldValue('credit_file_status', creditFileStatus).toLowerCase()}` : ''}
              </span>
            </div>
          </div>
        )}
        <CheckrFieldGrid data={hasScore ? rest : { ...rest, ...(creditFileStatus != null && { credit_file_status: creditFileStatus }) }} />
      </div>
    </section>
  );
}

/** Report page body, shared by the landlord's tenant report and the tenant's own report. */
export function CheckrReportView({
  report,
  isLoading,
  subtitleName,
  onBack,
  onDownload,
  isDownloading,
  onDownloadDocuments,
  isDownloadingDocuments,
  checkPackage: checkPackageProp,
  emptyMessage = 'No report available.',
}) {
  // Report sections may come at the top level or nested under `report`
  const reportBody = report?.report && typeof report.report === 'object' ? report.report : report;
  const result = String(report?.result ?? reportBody?.result ?? '').toLowerCase();
  const resultVariant = result === 'clear' || result === 'consider' ? result : 'neutral';
  const sections = getCheckrReportSections(reportBody);
  // Sections that need review start open; the landlord can expand the rest
  const [openOverrides, setOpenOverrides] = useState({});
  const isSectionOpen = (key) =>
    openOverrides[key] ?? String(getCheckrSectionStatus(reportBody?.[key]) ?? '').toLowerCase() === 'consider';
  const expandableKeys = sections
    .map(({ key }) => key)
    .filter((key) => reportBody?.[key] && typeof reportBody[key] === 'object');
  const allOpen = expandableKeys.length > 0 && expandableKeys.every(isSectionOpen);
  const toggleAll = () => setOpenOverrides(Object.fromEntries(expandableKeys.map((key) => [key, !allOpen])));
  // Income documents only exist for a completed Complete check. The package isn't always in the
  // response, so hide the button only when it's known to be Essential (the API returns 400 otherwise).
  // const checkPackage = report?.package ?? reportBody?.package ?? checkPackageProp;
  // const checkStatus = report?.status ?? reportBody?.status;
  // const showDocumentsDownload =
  //   !!onDownloadDocuments &&
  //   !!report &&
  //   checkPackage !== CHECKR_PACKAGE.ESSENTIAL &&
  //   (checkStatus == null || checkStatus === CHECKR_STATUS.COMPLETED);

  return (
    <div className="checkr-page checkr-print-area">
      <div className="screenings-actions-bar checkr-no-print" style={{ marginBottom: '20px' }}>
        <button type="button" className="screenings-back-button" onClick={onBack} aria-label="Go back">
          <span className="screenings-back-button-icon" aria-hidden="true">←</span>
        </button>
      </div>

      <div className="checkr-page-header">
        <div>
          <h1 className="checkr-page-title">Background Check Report</h1>
          <p className="checkr-report-subtitle">
            {subtitleName ? `${subtitleName} · ` : ''}Powered by Checkr
          </p>
        </div>
        <div className="checkr-page-download-actions checkr-no-print">
          {/* Income documents ZIP download, hidden for now
          {showDocumentsDownload && (
            <button
              type="button"
              className="tenant-detail-background-check-btn checkr-page-download"
              onClick={onDownloadDocuments}
              disabled={isLoading || isDownloadingDocuments}
            >
              {isDownloadingDocuments ? 'Downloading…' : 'Download ZIP'}
            </button>
          )}
          */}
          <button
            type="button"
            className="tenant-detail-background-check-btn checkr-page-download"
            onClick={onDownload}
            disabled={isLoading || !report || isDownloading}
          >
            {isDownloading ? 'Downloading…' : 'Download PDF'}
          </button>
        </div>
      </div>

      <div className="checkr-page-content">
        {isLoading ? (
          <p className="checkr-report-empty">Loading report…</p>
        ) : !report ? (
          <p className="checkr-report-empty">{emptyMessage}</p>
        ) : (
          <>
            <div className={`checkr-hero checkr-hero-${resultVariant}`}>
              <div className="checkr-hero-icon" aria-hidden="true">
                {resultVariant === 'clear' ? (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <div className="checkr-hero-text">
                <span className="checkr-hero-title">
                  {resultVariant === 'clear'
                    ? 'Clear — no adverse findings'
                    : resultVariant === 'consider'
                      ? 'Consider — review recommended'
                      : getCheckrStatusLabel(report.status)}
                </span>
                <span className="checkr-hero-meta">
                  {getCheckrStatusLabel(report.status)}
                  {report.checkr_order_id && <> · Order {report.checkr_order_id}</>}
                </span>
              </div>
            </div>
            {sections.length > 0 && (
              <div className="checkr-sections">
                <div className="checkr-sections-header">
                  <h2 className="checkr-sections-title">Report sections</h2>
                  {expandableKeys.length > 0 && (
                    <button type="button" className="checkr-sections-toggle-all checkr-no-print" onClick={toggleAll}>
                      {allOpen ? 'Collapse all' : 'Expand all'}
                    </button>
                  )}
                </div>
                {sections.map(({ key, label }) => (
                  <CheckrReportSection
                    key={key}
                    label={label}
                    data={reportBody?.[key]}
                    isOpen={isSectionOpen(key)}
                    onToggle={() => setOpenOverrides((prev) => ({ ...prev, [key]: !isSectionOpen(key) }))}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const BackgroundCheckReport = () => {
  const { id, tenantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Passed from TenantDetail; on a direct load / refresh they are re-read from the screening
  const [ids, setIds] = useState(() => {
    const state = location.state || {};
    return state.propertyId && state.tenantUserId
      ? {
          propertyId: state.propertyId,
          tenantUserId: state.tenantUserId,
          tenantName: state.tenantName || '',
          backgroundCheckPackage: state.backgroundCheckPackage ?? null,
        }
      : null;
  });
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isDownloadingDocuments, setIsDownloadingDocuments] = useState(false);

  const goBack = () => navigate(`/landlord/properties/${id}/tenant/${tenantId}`);

  useEffect(() => {
    if (ids || !tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await getApi(`/screening/${tenantId}`);
        const d = response?.data;
        const propertyId = d?.property_id || d?.property?.id || id;
        const tenantUserId = d?.tenant?.id ?? d?.tenant_id ?? null;
        if (cancelled) return;
        if (response?.statusCode === 200 && propertyId && tenantUserId) {
          setIds({
            propertyId,
            tenantUserId,
            tenantName: [d?.tenant?.first_name, d?.tenant?.last_name].filter(Boolean).join(' '),
            backgroundCheckPackage: d?.background_check_package ?? null,
          });
        } else {
          toast.error('Missing property or tenant information for the background check report.');
          setIsLoading(false);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching screening:', error);
        toast.error(error?.response?.data?.message || 'Failed to load tenant information.');
        setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ids, id, tenantId]);

  useEffect(() => {
    if (!ids) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const response = await getApi('/verification/tenant/checkr/verification-result', {
          params: { property_id: ids.propertyId, tenant_id: ids.tenantUserId },
        });
        if (cancelled) return;
        if (response?.statusCode === 200) {
          setReport(response?.data ?? null);
        } else {
          toast.error(response?.message || 'Failed to fetch background check report.');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching Checkr report:', error);
        toast.error(error?.response?.data?.message || 'Failed to fetch background check report. Please try again.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ids]);

  const handleDownloadPdf = async () => {
    if (!ids) return;
    setIsDownloadingPdf(true);
    try {
      const fallbackName = `background-check-${ids.tenantName || 'tenant'}.pdf`.replace(/\s+/g, '-');
      await downloadApi('/verification/tenant/checkr/report-pdf', fallbackName, {
        params: { property_id: ids.propertyId, tenant_id: ids.tenantUserId },
        headers: { Accept: 'application/pdf' },
      });
    } catch (error) {
      console.error('Error downloading Checkr report PDF:', error);
      toast.error(await getBlobErrorMessage(error, 'Failed to download the report PDF. Please try again.'));
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleDownloadDocuments = async () => {
    if (!ids) return;
    setIsDownloadingDocuments(true);
    try {
      const fallbackName = `income-documents-${ids.tenantName || 'tenant'}.zip`.replace(/\s+/g, '-');
      await downloadApi('/verification/tenant/checkr/report-documents', fallbackName, {
        params: { property_id: ids.propertyId, tenant_id: ids.tenantUserId },
        headers: { Accept: 'application/zip' },
      });
    } catch (error) {
      console.error('Error downloading income documents:', error);
      toast.error(await getBlobErrorMessage(error, 'Failed to download the income documents. Please try again.'));
    } finally {
      setIsDownloadingDocuments(false);
    }
  };

  return (
    <CheckrReportView
      report={report}
      isLoading={isLoading}
      subtitleName={ids?.tenantName}
      onBack={goBack}
      onDownload={handleDownloadPdf}
      isDownloading={isDownloadingPdf}
      onDownloadDocuments={handleDownloadDocuments}
      isDownloadingDocuments={isDownloadingDocuments}
      checkPackage={ids?.backgroundCheckPackage}
    />
  );
};

export default BackgroundCheckReport;
